import { ErrorMessage, useField } from 'formik';
import { isString, isNumber } from 'lodash-es';
import React, { useCallback } from 'react';
import { ActionMeta } from 'react-select';
import AsyncSelect from 'react-select/async';

import Error from 'components/Error';

import Components from './Components';
import * as Interfaces from './interfaces';
import * as Styled from './styled';

export function AutoComplete(props: Interfaces.ComponentProps) {
  const {
    name,
    label = '',
    minLength = 3,
    onChange = () => {},
    onBlur = () => {},
    openMenuOnClick = true,
    openMenuOnFocus = true,
    loadOptions,
    hint,
    ...asyncProps
  } = props;
  // Compose the formik field
  const [{ value: fieldValue }, meta, fieldHelpers] = useField<Interfaces.FieldValue>(name);
  /**
   * Does the component returns an error. We use a single error field boolean. This is due the need to support all cases of the FieldValue type (value with string or number, and of an object)
   */
  const hasError = meta.touched && meta.error ? true : false;

  /**
   * Helper function to resolve all available options to display (will call the consumer "loadOptions" handler provided as a prop to the <AutoComplete /> component)
   * @param inputValue User input into the search box
   */
  const resolveOptions = (inputValue = '') => {
    return inputValue.length >= minLength ? loadOptions(inputValue) : Promise.resolve([]);
  };

  // Resolve value object for the <Select /> component
  // -------------------------------------------------
  let value: Interfaces.Value;
  // String value result
  if (isString(fieldValue)) {
    value = {
      label: fieldValue.trim(),
      value: fieldValue.trim()
    };
  } // Number value result
  else if (isNumber(fieldValue)) {
    value = {
      label: fieldValue.toString(),
      value: fieldValue.toString()
    };
  } // All other options (object | array)
  else {
    value = fieldValue;
  }

  /**
   * Event handler for when the "select" value gets changed
   * ref: https://github.com/jaredpalmer/formik/issues/789
   */
  const onChangeMemo = useCallback(
    (selectedValue: Interfaces.Value, meta: ActionMeta<Interfaces.OptionType>) => {
      // Use Formik internal "setValue" helper method
      fieldHelpers.setValue(selectedValue);
      // Call consumer component "onChange" event prop
      onChange(selectedValue, meta);
    },
    [fieldHelpers, onChange]
  );

  const onBlurMemo = useCallback(
    (evt: React.FocusEvent) => {
      fieldHelpers.setTouched(true, false);
      onBlur(evt);
    },
    [onBlur, fieldHelpers]
  );

  return (
    <Styled.Wrapper>
      <Styled.Label htmlFor={name} id={`${name}.label`}>
        {label}
      </Styled.Label>
      <AsyncSelect
        aria-label={label}
        id={name}
        name={name}
        classNamePrefix={`Select_${name}`}
        openMenuOnClick={openMenuOnClick}
        openMenuOnFocus={openMenuOnFocus}
        value={value}
        onChange={onChangeMemo}
        onBlur={onBlurMemo}
        loadOptions={resolveOptions}
        styles={Styled.Select(hasError)}
        components={Components({ ...props, value })}
        {...asyncProps}
        // <AsyncSelect /> "props" not needed, as we using "Formik" internal logic for setting the "value" prop
        // defaultValue
        // defaultInputValue
      />
      {hint && <Styled.HintLabel>{hint}</Styled.HintLabel>}
      <Styled.ErrorWrapper>
        <ErrorMessage name={name}>
          {msg => (
            <Error
              aria-invalid={true}
              aria-labelledby={`${name}.label`}
              data-testid={`${name}-AutoCompleteError`}>
              {msg}
            </Error>
          )}
        </ErrorMessage>
      </Styled.ErrorWrapper>
    </Styled.Wrapper>
  );
}

export default AutoComplete;
